import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { BsBug, BsLightbulb, BsArrowUp, BsQuestionCircle, BsThreeDots, BsX, BsCamera, BsImage, BsTrash, BsCloudUpload } from 'react-icons/bs';
import axios from 'axios';
import { isTeamMemberAuthenticated, getTeamUser } from '../../services/teamAuthService';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  background: linear-gradient(135deg, #1a1f2e 0%, #0f1419 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from { 
      opacity: 0; 
      transform: translateY(20px); 
    }
    to { 
      opacity: 1; 
      transform: translateY(0); 
    }
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.5);
    border-radius: 4px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h2`
  color: #fff;
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #6366f1;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const TypeSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TypeButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 12px;
  background: ${props => props.$active ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  border: 2px solid ${props => props.$active ? '#6366f1' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.5);
  }

  svg {
    font-size: 1.5rem;
    color: ${props => props.$color || '#6366f1'};
  }

  span {
    font-size: 0.75rem;
    font-weight: 500;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #fff;
  font-size: 0.95rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #6366f1;
    background: rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #fff;
  font-size: 0.95rem;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #6366f1;
    background: rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const PrioritySelector = styled.div`
  display: flex;
  gap: 10px;
`;

const PriorityButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: ${props => props.$active ? props.$bgColor : 'rgba(255, 255, 255, 0.05)'};
  border: 2px solid ${props => props.$active ? props.$borderColor : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$bgColor};
    border-color: ${props => props.$borderColor};
  }
`;

const ContextInfo = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
`;

const ContextTitle = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const ContextItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const ContextLabel = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
`;

const ContextValue = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SubmitButton = styled(Button)`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  color: #fff;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px 20px;

  svg {
    font-size: 4rem;
    color: #10b981;
    margin-bottom: 20px;
  }

  h3 {
    color: #fff;
    font-size: 1.5rem;
    margin-bottom: 12px;
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  color: #ef4444;
  font-size: 0.875rem;
`;

const ImageUploadArea = styled.div`
  border: 2px dashed ${props => props.$isDragging ? '#6366f1' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  background: ${props => props.$isDragging ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
    background: rgba(99, 102, 241, 0.05);
  }
`;

const UploadIcon = styled.div`
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 10px;
`;

const UploadText = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
`;

const UploadHint = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.75rem;
  margin-top: 6px;
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  margin-top: 16px;
`;

const ImagePreviewItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.9);
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #ef4444;
    transform: scale(1.1);
  }
`;

const ImageUploadProgress = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: rgba(0, 0, 0, 0.5);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.$progress}%;
    background: linear-gradient(90deg, #6366f1, #8b5cf6);
    transition: width 0.3s ease;
  }
`;

const feedbackTypes = [
  { id: 'bug', label: 'Bug', icon: BsBug, color: '#ef4444' },
  { id: 'suggestion', label: 'Suggestion', icon: BsLightbulb, color: '#f59e0b' },
  { id: 'improvement', label: 'Improvement', icon: BsArrowUp, color: '#10b981' },
  { id: 'question', label: 'Question', icon: BsQuestionCircle, color: '#3b82f6' },
  { id: 'other', label: 'Other', icon: BsThreeDots, color: '#8b5cf6' }
];

const priorities = [
  { id: 'low', label: 'Low', bgColor: 'rgba(16, 185, 129, 0.2)', borderColor: '#10b981' },
  { id: 'medium', label: 'Medium', bgColor: 'rgba(245, 158, 11, 0.2)', borderColor: '#f59e0b' },
  { id: 'high', label: 'High', bgColor: 'rgba(239, 68, 68, 0.2)', borderColor: '#ef4444' },
  { id: 'critical', label: 'Critical', bgColor: 'rgba(220, 38, 127, 0.2)', borderColor: '#dc2680' }
];

const FeedbackModal = ({ isOpen, onClose, companyName: propCompanyName }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const fileInputRef = useRef(null);
  
  const [type, setType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Image upload state
  const [images, setImages] = useState([]); // { file, preview, uploading, uploaded, url, error }
  const [isDragging, setIsDragging] = useState(false);
  
  const [contextInfo, setContextInfo] = useState({
    currentPage: '',
    currentUrl: '',
    browserInfo: '',
    screenResolution: ''
  });
  
  const [expertInfo, setExpertInfo] = useState({
    expertId: '',
    expertName: ''
  });

  useEffect(() => {
    if (isOpen) {
      // Gather context information
      const pageName = getPageName(location.pathname);
      setContextInfo({
        currentPage: pageName,
        currentUrl: window.location.href,
        browserInfo: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      });
      
      // Fetch expert info
      const fetchExpertInfo = async () => {
        try {
          const token = await getAccessTokenSilently();
          const response = await axios.get(`${process.env.REACT_APP_SERVER}profile`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.data) {
            setExpertInfo({
              expertId: response.data._id || response.data.userId || '',
              expertName: response.data.name || response.data.companyName || propCompanyName || ''
            });
          } else if (propCompanyName) {
            // Use company name from header if profile didn't return one
            setExpertInfo(prev => ({
              ...prev,
              expertName: propCompanyName
            }));
          }
        } catch (err) {
          console.log('Could not fetch expert info:', err);
        }
      };
      fetchExpertInfo();
      
      // Reset form
      setType('bug');
      setTitle('');
      setDescription('');
      setPriority('medium');
      setImages([]);
      setIsSuccess(false);
      setError(null);
    }
  }, [isOpen, location.pathname, getAccessTokenSilently]);

  // Cleanup image previews on unmount
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
    };
  }, [images]);

  const handleFileSelect = async (files) => {
    const validFiles = Array.from(files).filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      return isImage && isValidSize;
    });

    if (validFiles.length === 0) {
      setError('Please select valid image files (max 10MB each)');
      return;
    }

    if (images.length + validFiles.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
      uploaded: false,
      url: null,
      error: null
    }));

    setImages(prev => [...prev, ...newImages]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveImage = (index) => {
    setImages(prev => {
      const newImages = [...prev];
      if (newImages[index].preview) {
        URL.revokeObjectURL(newImages[index].preview);
      }
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const uploadImage = async (imageData, index, token) => {
    try {
      setImages(prev => {
        const newImages = [...prev];
        newImages[index] = { ...newImages[index], uploading: true };
        return newImages;
      });

      const formData = new FormData();
      formData.append('image', imageData.file);

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}bug-reports/upload-image`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImages(prev => {
        const newImages = [...prev];
        newImages[index] = {
          ...newImages[index],
          uploading: false,
          uploaded: true,
          url: response.data.url
        };
        return newImages;
      });

      return response.data.url;
    } catch (err) {
      console.error('Image upload error:', err);
      setImages(prev => {
        const newImages = [...prev];
        newImages[index] = {
          ...newImages[index],
          uploading: false,
          error: 'Failed to upload'
        };
        return newImages;
      });
      return null;
    }
  };

  const getPageName = (pathname) => {
    const routes = {
      '/main': 'Workspace',
      '/profile': 'Profile',
      '/settings/tokens': 'Token Settings',
      '/settings/documents': 'Documents',
      '/settings/onboarding': 'Onboarding',
      '/settings/firm-standards': 'Firm Standards',
      '/admin': 'Admin Panel',
      '/contact-us': 'Contact Us'
    };
    
    // Check for exact match first
    if (routes[pathname]) return routes[pathname];
    
    // Check for partial matches
    for (const [route, name] of Object.entries(routes)) {
      if (pathname.startsWith(route)) return name;
    }
    
    return pathname;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Please enter a title for your feedback');
      return;
    }
    
    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      
      // Upload all images first
      const screenshotUrls = [];
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        if (img.uploaded && img.url) {
          // Already uploaded
          screenshotUrls.push(img.url);
        } else if (!img.error) {
          // Need to upload
          const url = await uploadImage(img, i, token);
          if (url) {
            screenshotUrls.push(url);
          }
        }
      }
      
      // Check if a team member is logged in
      const isMemberAuth = isTeamMemberAuthenticated();
      const teamMember = isMemberAuth ? getTeamUser() : null;
      
      // Use team member info if available, otherwise use Auth0 user info
      const submitterName = teamMember?.name || teamMember?.fullName || user.name;
      const submitterEmail = teamMember?.email || user.email;
      const submitterId = teamMember?._id || user.sub;
      
      const feedbackData = {
        type,
        title: title.trim(),
        description: description.trim(),
        priority,
        userId: submitterId,
        userEmail: submitterEmail,
        userName: submitterName,
        expertId: expertInfo.expertId,
        expertName: expertInfo.expertName || propCompanyName || '',
        screenshots: screenshotUrls,
        ...contextInfo
      };

      const response = await fetch(`${process.env.REACT_APP_SERVER}bug-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(feedbackData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setIsSuccess(true);
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        onClose();
      }, 3000);
      
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <BsBug />
            Send Feedback
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <BsX size={24} />
          </CloseButton>
        </ModalHeader>

        {isSuccess ? (
          <ModalBody>
            <SuccessMessage>
              <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <h3>Thank You!</h3>
              <p>
                Your feedback has been submitted successfully. We appreciate your input and will review it shortly.
              </p>
            </SuccessMessage>
          </ModalBody>
        ) : (
          <form onSubmit={handleSubmit}>
            <ModalBody>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              
              <FormGroup>
                <Label>What type of feedback is this?</Label>
                <TypeSelector>
                  {feedbackTypes.map((t) => (
                    <TypeButton
                      key={t.id}
                      type="button"
                      $active={type === t.id}
                      $color={t.color}
                      onClick={() => setType(t.id)}
                    >
                      <t.icon />
                      <span>{t.label}</span>
                    </TypeButton>
                  ))}
                </TypeSelector>
              </FormGroup>

              <FormGroup>
                <Label>Title *</Label>
                <Input
                  type="text"
                  placeholder="Brief summary of your feedback"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                />
              </FormGroup>

              <FormGroup>
                <Label>Description *</Label>
                <TextArea
                  placeholder="Please describe in detail. For bugs, include steps to reproduce the issue..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={5000}
                />
              </FormGroup>

              <FormGroup>
                <Label>Priority</Label>
                <PrioritySelector>
                  {priorities.map((p) => (
                    <PriorityButton
                      key={p.id}
                      type="button"
                      $active={priority === p.id}
                      $bgColor={p.bgColor}
                      $borderColor={p.borderColor}
                      onClick={() => setPriority(p.id)}
                    >
                      {p.label}
                    </PriorityButton>
                  ))}
                </PrioritySelector>
              </FormGroup>

              <FormGroup>
                <Label>Screenshots (Optional)</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
                <ImageUploadArea
                  $isDragging={isDragging}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <UploadIcon>
                    <BsCloudUpload size={32} />
                  </UploadIcon>
                  <UploadText>
                    Click or drag images here to upload
                  </UploadText>
                  <UploadHint>
                    Max 5 images, 10MB each • PNG, JPG, GIF, WebP
                  </UploadHint>
                </ImageUploadArea>
                
                {images.length > 0 && (
                  <ImagePreviewGrid>
                    {images.map((img, index) => (
                      <ImagePreviewItem key={index}>
                        <img src={img.preview} alt={`Screenshot ${index + 1}`} />
                        <RemoveImageButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(index);
                          }}
                        >
                          <BsTrash size={12} />
                        </RemoveImageButton>
                        {img.uploading && <ImageUploadProgress $progress={50} />}
                        {img.uploaded && (
                          <div style={{
                            position: 'absolute',
                            bottom: '4px',
                            left: '4px',
                            background: 'rgba(16, 185, 129, 0.9)',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            ✓
                          </div>
                        )}
                        {img.error && (
                          <div style={{
                            position: 'absolute',
                            bottom: '4px',
                            left: '4px',
                            background: 'rgba(239, 68, 68, 0.9)',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}>
                            ✕
                          </div>
                        )}
                      </ImagePreviewItem>
                    ))}
                  </ImagePreviewGrid>
                )}
              </FormGroup>

              <ContextInfo>
                <ContextTitle>Context Information (Auto-captured)</ContextTitle>
                <ContextItem>
                  <ContextLabel>Current Page</ContextLabel>
                  <ContextValue>{contextInfo.currentPage}</ContextValue>
                </ContextItem>
                <ContextItem>
                  <ContextLabel>URL</ContextLabel>
                  <ContextValue>{contextInfo.currentUrl}</ContextValue>
                </ContextItem>
                <ContextItem>
                  <ContextLabel>Screen Resolution</ContextLabel>
                  <ContextValue>{contextInfo.screenResolution}</ContextValue>
                </ContextItem>
              </ContextInfo>
            </ModalBody>

            <ModalFooter>
              <CancelButton type="button" onClick={onClose}>
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </SubmitButton>
            </ModalFooter>
          </form>
        )}
      </Modal>
    </Overlay>
  );
};

export default FeedbackModal;

